import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';

interface PageHeaderProps {
  title: string;
  titleAr?: string;
  subtitle?: string;
  subtitleAr?: string;
  pattern?: 'geometric' | 'stars' | 'none';
  size?: 'sm' | 'md' | 'lg';
  children?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  titleAr,
  subtitle,
  subtitleAr,
  pattern = 'geometric',
  size = 'md',
  children,
}) => {
  const { isRTL } = useLanguage();

  const displayTitle = isRTL && titleAr ? titleAr : title;
  const displaySubtitle = isRTL && subtitleAr ? subtitleAr : subtitle;

  const sizeClasses = {
    sm: 'py-10 md:py-14',
    md: 'py-14 md:py-20',
    lg: 'py-20 md:py-28',
  };

  return (
    <section
      className={`relative overflow-hidden bg-hero-gradient dark:bg-hero-gradient-dark ${sizeClasses[size]}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Pattern overlay */}
      {pattern !== 'none' && (
        <div
          className={`absolute inset-0 pointer-events-none ${
            pattern === 'stars' ? 'star-pattern-overlay' : 'islamic-pattern-overlay'
          }`}
          style={{ opacity: 0.5 }}
        />
      )}

      {/* Gradient orbs */}
      <div className="absolute -top-1/2 -end-1/4 w-[600px] h-[600px] rounded-full bg-amber-400/5 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-1/2 -start-1/4 w-[400px] h-[400px] rounded-full bg-emerald-400/5 blur-3xl pointer-events-none" />

      <div className="container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">
            {displayTitle}
          </h1>
          {displaySubtitle && (
            <p className="text-emerald-100/80 text-lg md:text-xl max-w-2xl">
              {displaySubtitle}
            </p>
          )}
          {children && <div className="mt-6">{children}</div>}
        </motion.div>
      </div>
    </section>
  );
};

export default PageHeader;
